import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTheme, useMediaQuery } from '@mui/material';

/**
 * PageHeader — standard screen header.
 *
 * Props:
 *   title      {string}    — page title (required)
 *   back       {boolean}   — show back arrow button
 *   onBack     {function}  — back button handler
 *   actions    {node|node[]} — action nodes (rendered inline on desktop,
 *                              collapsed into overflow menu on mobile)
 *   breadcrumbs {node}     — optional breadcrumb trail above the title
 *   sx         {object}    — extra MUI sx overrides
 *
 * On mobile (< md) any actions whose element type is identified as an
 * `<Action>` component (has __isAction flag) are folded into a MoreVert
 * overflow menu. Plain nodes are always rendered inline.
 *
 * Usage:
 *   <PageHeader
 *     title="Invoices"
 *     back
 *     onBack={() => navigate(-1)}
 *     actions={<>
 *       <Action primary icon={<Add />} label="Add invoice" onClick={...} />
 *       <Action icon={<FileDownload />} label="Export" onClick={...} />
 *     </>}
 *   />
 */
export default function PageHeader({
  title,
  back = false,
  onBack,
  actions,
  breadcrumbs,
  sx,
  ...rest
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);

  // Collect Action children for the overflow menu on mobile.
  // Non-Action nodes (e.g. a plain Button the caller controls) stay inline.
  const actionNodes = React.Children.toArray(actions);
  const overflowActions = isMobile
    ? actionNodes.filter((node) => node?.type?.__isAction && !node?.props?.primary)
    : [];
  const inlineActions = isMobile
    ? actionNodes.filter(
        (node) => !node?.type?.__isAction || node?.props?.primary
      )
    : actionNodes;

  const handleOverflowOpen = (e) => setAnchorEl(e.currentTarget);
  const handleOverflowClose = () => setAnchorEl(null);

  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: { xs: 1, sm: 2 },
        py: 1.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0, // never shrink — only PageContent should grow/shrink
        ...sx,
      }}
      {...rest}
    >
      {/* Breadcrumbs + title */}
      <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
        {breadcrumbs && (
          <Box sx={{ mb: 0.25 }}>{breadcrumbs}</Box>
        )}
        <Typography
          variant="h5"
          component="h1"
          noWrap
          sx={{ fontWeight: 600, lineHeight: 1.3 }}
        >
          {title}
        </Typography>
      </Box>

      {/* Inline actions (always shown, or primary on mobile) */}
      {inlineActions.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
          {inlineActions}
        </Box>
      )}

      {/* Overflow menu trigger — only shown on mobile when there are overflow actions */}
      {isMobile && overflowActions.length > 0 && (
        <>
          <Tooltip title="More actions">
            <IconButton size="small" onClick={handleOverflowOpen}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleOverflowClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {overflowActions.map((node, i) => {
              const { icon, label, onClick, dividerBefore } = node.props ?? {};
              return (
                <React.Fragment key={i}>
                  {dividerBefore && <Divider />}
                  <MenuItem
                    onClick={() => {
                      handleOverflowClose();
                      onClick?.();
                    }}
                  >
                    {icon && <ListItemIcon>{icon}</ListItemIcon>}
                    <ListItemText>{label}</ListItemText>
                  </MenuItem>
                </React.Fragment>
              );
            })}
          </Menu>
        </>
      )}

      {/* Back button - placed at the far right for easier right-thumb reach */}
      {back && (
        isMobile ? (
          <Tooltip title="Back">
            <IconButton onClick={onBack} size="small" sx={{ ml: 0.5, flexShrink: 0 }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon fontSize="small" />}
            onClick={onBack}
            size="medium"
            sx={{ ml: 0.5, flexShrink: 0 }}
          >
            Back
          </Button>
        )
      )}
    </Box>
  );
}
